// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Rate Schedule Management
// FEMA-compliant rate schedules per contractor with distance tiers
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  TextInput, Table, ActionIcon, Modal,
} from '../../components/gab/GabComponents';
import { IconPlus, IconRefresh, IconTrash, IconDownload, IconUpload } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../../lib/api-switch';

// Standard FEMA line items (NC prepositioned contract template)
const DEFAULT_LINE_ITEMS = [
  { code: '1',  desc: 'Vegetative/C&D removal from ROW → DMS',                       unit: 'CY' },
  { code: '2',  desc: 'Vegetative/C&D removal from private property → DMS',           unit: 'CY' },
  { code: '3',  desc: 'Vegetative/C&D removal from ROW → direct to final disposal',   unit: 'CY' },
  { code: '4',  desc: 'Haul from DMS → final disposal site',                          unit: 'CY' },
  { code: '5',  desc: 'DMS/TDSRS management',                                         unit: 'CY' },
  { code: '6',  desc: 'Grinding/chipping vegetative at DMS',                          unit: 'CY' },
  { code: '7a', desc: 'Reduction by chipping/grinding at DMS',                        unit: 'CY' },
  { code: '7b', desc: 'Reduction by compaction/consolidation (C&D) at DMS',           unit: 'CY' },
  { code: '8',  desc: 'Open burning of vegetative debris at DMS',                     unit: 'CY' },
  { code: '9',  desc: 'Air curtain incineration at DMS',                              unit: 'CY' },
  { code: '10', desc: 'White goods pickup & haul to disposal',                        unit: 'LB' },
  { code: '11', desc: 'Household hazardous waste disposal',                           unit: 'LB' },
  { code: '12', desc: 'Freon management & recycling',                                 unit: 'UNIT' },
  { code: '13', desc: 'Biowaste transportation & disposal',                           unit: 'LB' },
  { code: '14', desc: 'Stump extraction & disposal (24"–47.99" diameter)',             unit: 'STUMP' },
  { code: '15', desc: 'Stump extraction & disposal (48"+ diameter)',                   unit: 'STUMP' },
  { code: '16', desc: 'Boulder removal (18"–35.99" diameter)',                         unit: 'EACH' },
  { code: '17', desc: 'Boulder removal (36"+ diameter)',                               unit: 'EACH' },
  { code: '18', desc: 'Hazardous hanging limbs (2"+ at break)',                       unit: 'TREE' },
  { code: '19', desc: 'Hazardous standing trees (6"–12.99" diameter)',                unit: 'EACH' },
  { code: '20', desc: 'Hazardous standing trees (13"–24.99" diameter)',               unit: 'EACH' },
  { code: '21', desc: 'Hazardous standing trees (25"–36.99" diameter)',               unit: 'EACH' },
  { code: '22', desc: 'Hazardous standing trees (37"–48.99" diameter)',               unit: 'EACH' },
  { code: '23', desc: 'Hazardous standing trees (49"+ diameter)',                     unit: 'EACH' },
  { code: '24', desc: 'Canal/waterway/ditch clearing',                                unit: 'LF' },
  { code: '25', desc: 'Bays and open waters',                                         unit: 'ACRE' },
  { code: '26', desc: 'Marine vessel/land vehicle removal',                           unit: 'LF' },
];

const TIER_LABELS = ['0–15 mi', '16–30 mi', '31–60 mi', '60+ mi'];

interface RateRow {
  id: number;
  incidentId: string;
  contractorId: string;
  lineItemCode: string;
  description: string;
  unit: string;
  baseRate: string;
  tier2Rate: string;
  tier3Rate: string;
  tier4Rate: string;
  notes: string;
}

const TK = TABLES.debrisRateSchedules;
const FL = FIELDS.debrisRateSchedules;

export default function RateSchedule() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, token } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const { contractors, loadContractors } = useDebrisStore();

  const [rates, setRates] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);

  const iContractors = incidentId ? contractors : [];
  const contractorSelect = useMemo(() => iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })), [iContractors]);

  const loadRates = async () => {
    if (!TK) return;
    setLoading(true);
    try {
      const rows = await fetchTable(TK, { token: token ?? undefined });
      setRates(rows.map((r: any) => ({
        id: Number(r.record_id || r.id) || 0,
        incidentId: '',
        contractorId: String(r[FL.contractorId] || ''),
        lineItemCode: String(r[FL.lineItemCode] || ''),
        description: String(r[FL.description] || ''),
        unit: String(r[FL.unit] || ''),
        baseRate: String(r[FL.rateTier1] || ''),
        tier2Rate: String(r[FL.rateTier2] || ''),
        tier3Rate: String(r[FL.rateTier3] || ''),
        tier4Rate: String(r[FL.rateTier4] || ''),
        notes: String(r[FL.notes] || ''),
      })));
    } catch { /* table may not exist */ }
    setLoading(false);
  };

  useEffect(() => { loadContractors(); loadRates(); }, [loadContractors]);

  const contractorRates = useMemo(() => {
    if (!selectedContractor) return [];
    return rates
      .filter(r => r.contractorId === selectedContractor)
      .sort((a, b) => {
        const na = parseFloat(a.lineItemCode) || 999;
        const nb = parseFloat(b.lineItemCode) || 999;
        return na - nb || a.lineItemCode.localeCompare(b.lineItemCode);
      });
  }, [rates, selectedContractor, incidentId]);

  // Seed default line items for a contractor
  const seedRates = async () => {
    if (!selectedContractor || !TK) return;
    setSaving(true);
    try {
      let count = 0;
      for (const item of DEFAULT_LINE_ITEMS) {
        // Skip if already exists
        if (contractorRates.some(r => r.lineItemCode === item.code)) continue;
        const fields: Array<[string, string]> = [
          [FL.contractorId, selectedContractor],
          [FL.lineItemCode, item.code],
          [FL.description, item.desc],
          [FL.unit, item.unit],
          [FL.rateTier1, ''],
          [FL.rateTier2, ''],
          [FL.rateTier3, ''],
          [FL.rateTier4, ''],
          [FL.notes, ''],
        ];
        await createRecord(TK, fields, token ?? undefined);
        count++;
      }
      notifications.show({ message: `Seeded ${count} line items`, color: 'var(--color-success)' });
      await loadRates();
      setSeedConfirmOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Seed failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  // Map UI field names to V2 FIELDS keys
  const RATE_FIELD_MAP: Record<string, string> = {
    baseRate: FL.rateTier1, tier2Rate: FL.rateTier2, tier3Rate: FL.rateTier3, tier4Rate: FL.rateTier4,
  };

  // Update a single rate cell
  const updateRate = async (row: RateRow, field: 'baseRate' | 'tier2Rate' | 'tier3Rate' | 'tier4Rate', value: string) => {
    // Optimistic update
    setRates(prev => prev.map(r => r.id === row.id ? { ...r, [field]: value } : r));
    try {
      const fieldKey = RATE_FIELD_MAP[field];
      await updateRecord(TK, row.id, [[fieldKey, value]], token ?? undefined);
    } catch {
      notifications.show({ message: 'Failed to save rate', color: 'var(--color-error)' });
      loadRates(); // Revert
    }
  };

  // Delete a line item
  const deleteRate = async (row: RateRow) => {
    try {
      await deleteRecord(TK, row.id, token ?? undefined);
      setRates(prev => prev.filter(r => r.id !== row.id));
    } catch {
      notifications.show({ message: 'Delete failed', color: 'var(--color-error)' });
    }
  };

  // Export as CSV
  const exportCSV = () => {
    if (contractorRates.length === 0) return;
    const contractor = iContractors.find(c => String(c.id) === selectedContractor);
    let csv = `Rate Schedule — ${contractor?.companyName || 'Unknown'}\n`;
    csv += `Item,Description,Unit,Base (${TIER_LABELS[0]}),Tier 2 (${TIER_LABELS[1]}),Tier 3 (${TIER_LABELS[2]}),Tier 4 (${TIER_LABELS[3]}),Notes\n`;
    contractorRates.forEach(r => {
      csv += `"${r.lineItemCode}","${r.description}","${r.unit}",${r.baseRate || ''},${r.tier2Rate || ''},${r.tier3Rate || ''},${r.tier4Rate || ''},"${r.notes || ''}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rate-schedule-${contractor?.companyName?.replace(/\s+/g, '-') || 'contractor'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import CSV
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const importCSV = async (file: File) => {
    if (!selectedContractor || !TK) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      // Skip header rows (first 2 lines)
      const dataLines = lines.slice(2);
      let updated = 0;
      for (const line of dataLines) {
        const cols = line.match(/(".*?"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, '').trim()) ?? [];
        if (cols.length < 4) continue;
        const [code, , , baseR, tier2R, tier3R, tier4R] = cols;
        if (!code) continue;
        const existing = contractorRates.find(r => r.lineItemCode === code);
        const fields: Array<[string, string]> = [
          [FL.rateTier1, baseR ?? ''],
          [FL.rateTier2, tier2R ?? ''],
          [FL.rateTier3, tier3R ?? ''],
          [FL.rateTier4, tier4R ?? ''],
        ];
        if (existing) {
          await updateRecord(TK, existing.id, fields, token ?? undefined);
        } else {
          const item = DEFAULT_LINE_ITEMS.find(i => i.code === code);
          await createRecord(TK, [
            [FL.contractorId, selectedContractor],
            [FL.lineItemCode, code],
            [FL.description, item?.desc ?? cols[1] ?? ''],
            [FL.unit, item?.unit ?? cols[2] ?? ''],
            ...fields,
            [FL.notes, ''],
          ], token ?? undefined);
        }
        updated++;
      }
      notifications.show({ message: `Imported ${updated} rate rows`, color: 'var(--color-success)' });
      await loadRates();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'var(--color-error)' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const contractorName = selectedContractor ? (iContractors.find(c => String(c.id) === selectedContractor)?.companyName || '') : '';

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Rate Schedule</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={loading} onClick={loadRates}>Refresh</Button>
          {contractorRates.length > 0 && (
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>Export CSV</Button>
          )}
          {canEdit && selectedContractor && (
            <>
              <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) importCSV(f); }} />
              <Button size="xs" variant="light" leftSection={<IconUpload size={14} />}
                loading={importing} onClick={() => fileInputRef.current?.click()}>
                Import CSV
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        FEMA-compliant rate schedules with distance-based tier pricing. Select a contractor to view or edit their rates.
      </Text>

      {/* Contractor selector */}
      <Group gap="sm">
        <Select label="Contractor" data={contractorSelect} value={selectedContractor}
          onChange={v => setSelectedContractor(v)} searchable placeholder="Select contractor..."
          style={{ flex: 1, maxWidth: 400 }} />
        {canEdit && selectedContractor && contractorRates.length === 0 && (
          <Button mt={24} size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => setSeedConfirmOpen(true)}>
            Initialize Rate Schedule
          </Button>
        )}
      </Group>

      {/* Distance tier legend */}
      {selectedContractor && (
        <Paper p="sm" radius="md" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb={4}>Distance Tiers (pickup zone → disposal site)</Text>
          <Group gap="md">
            {TIER_LABELS.map((label, i) => (
              <Badge key={i} size="sm" variant="light" color={['green', 'var(--color-primary-700)', '#F86A0B', 'red'][i]}>
                Tier {i + 1}: {label}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      {/* Rate table */}
      {selectedContractor && contractorRates.length > 0 && (
        <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
          <Table striped withTableBorder style={{ fontSize: '12px' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 50 }}>Item</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th style={{ width: 60 }}>Unit</Table.Th>
                <Table.Th ta="center" style={{ width: 100, background: 'rgba(34,197,94,0.08)' }}>Tier 1<br/><Text size="xs" c="dimmed">{TIER_LABELS[0]}</Text></Table.Th>
                <Table.Th ta="center" style={{ width: 100, background: 'rgba(59,130,246,0.08)' }}>Tier 2<br/><Text size="xs" c="dimmed">{TIER_LABELS[1]}</Text></Table.Th>
                <Table.Th ta="center" style={{ width: 100, background: 'rgba(249,115,22,0.08)' }}>Tier 3<br/><Text size="xs" c="dimmed">{TIER_LABELS[2]}</Text></Table.Th>
                <Table.Th ta="center" style={{ width: 100, background: 'rgba(239,68,68,0.08)' }}>Tier 4<br/><Text size="xs" c="dimmed">{TIER_LABELS[3]}</Text></Table.Th>
                {canEdit && <Table.Th style={{ width: 30 }}></Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contractorRates.map(row => (
                <Table.Tr key={row.id}>
                  <Table.Td fw={600}>{row.lineItemCode}</Table.Td>
                  <Table.Td><Text size="xs">{row.description}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{row.unit}</Badge></Table.Td>
                  {(['baseRate', 'tier2Rate', 'tier3Rate', 'tier4Rate'] as const).map((field, i) => {
                    const rawVal = row[field] || '';
                    const numVal = parseFloat(String(rawVal));
                    const displayVal = rawVal && isFinite(numVal) ? numVal.toFixed(2) : '';
                    return (
                    <Table.Td key={field} style={{ background: ['rgba(34,197,94,0.04)', 'rgba(59,130,246,0.04)', 'rgba(249,115,22,0.04)', 'rgba(239,68,68,0.04)'][i] }}>
                      {canEdit ? (
                        <TextInput size="xs" variant="unstyled"
                          value={displayVal}
                          onChange={e => setRates(prev => prev.map(r => r.id === row.id ? { ...r, [field]: e.target.value } : r))}
                          onBlur={e => updateRate(row, field, e.target.value)}
                          placeholder="$0.00"
                          styles={{ input: { textAlign: 'right', fontSize: 12, padding: '2px 8px' } }} />
                      ) : (
                        <Text size="xs" ta="right">{rawVal && isFinite(numVal) ? '$' + numVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</Text>
                      )}
                    </Table.Td>
                    );
                  })}
                  {canEdit && (
                    <Table.Td>
                      <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => deleteRate(row)}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {selectedContractor && contractorRates.length === 0 && !loading && (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <Text c="dimmed">No rate schedule for {contractorName}.</Text>
          {canEdit && (
            <Button mt="sm" size="xs" onClick={() => setSeedConfirmOpen(true)}>
              Initialize with FEMA Standard Line Items
            </Button>
          )}
        </Paper>
      )}

      {!selectedContractor && (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <Text c="dimmed">Select a contractor to view or manage their rate schedule.</Text>
        </Paper>
      )}

      {/* Seed confirmation */}
      <Modal opened={seedConfirmOpen} onClose={() => setSeedConfirmOpen(false)}
        title="Initialize Rate Schedule" centered size="sm">
        <Stack>
          <Text size="sm">
            Create a rate schedule for <strong>{contractorName}</strong> with the 26 standard FEMA line items?
          </Text>
          <Text size="xs" c="dimmed">
            This adds all standard debris removal line items (hauling, DMS operations, stumps, trees, white goods, HHW, marine debris, etc.) with blank rates for you to fill in. Distance tiers are: 0–15 mi, 16–30 mi, 31–60 mi, 60+ mi.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setSeedConfirmOpen(false)}>Cancel</Button>
            <Button size="xs" loading={saving} onClick={seedRates}>Initialize</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
